import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../../services/api';
import type { Chat, ScheduledMessage } from '../../../types';

type Props = {
  chats: Chat[];
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const statusLabel: Record<ScheduledMessage['status'], string> = {
  pending: 'Wachtend',
  sent: 'Verzonden',
  failed: 'Mislukt',
  cancelled: 'Geannuleerd',
};

const SchedulePanel = ({ chats }: Props) => {
  const [items, setItems] = useState<ScheduledMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatId, setChatId] = useState('');
  const [content, setContent] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);
  const [minDateTime] = useState(() =>
    new Date(Date.now() + 60_000).toISOString().slice(0, 16),
  );

  const load = useCallback(async () => {
    try {
      const data = await api.scheduledMessages();
      setItems(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!chatId || !content.trim() || !scheduledAt) return;
    setSaving(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      const created = await api.createScheduledMessage({
        chatId,
        content: content.trim(),
        scheduledAt: iso,
      });
      setItems((current) =>
        [...current, created].sort(
          (a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt),
        ),
      );
      setContent('');
      setScheduledAt('');
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      const cancelled = await api.cancelScheduledMessage(id);
      setItems((current) => current.map((item) => (item.id === id ? cancelled : item)));
    } catch {
      // ignore
    }
  };

  const chatTitle = (id: string): string =>
    chats.find((c) => c.id === id)?.title ?? id.slice(0, 8);

  return (
    <div className="schedule-panel">
      <p className="tool-description">
        Plan berichten in om op een specifiek tijdstip automatisch te worden verstuurd. De
        scheduler controleert elke 30 seconden of er berichten due zijn.
      </p>
      <div className="schedule-form">
        <label className="tool-field">
          <span>Chat</span>
          <select value={chatId} onChange={(event) => setChatId(event.target.value)}>
            <option value="">Selecteer een chat…</option>
            {chats.map((chat) => (
              <option key={chat.id} value={chat.id}>
                {chat.title}
              </option>
            ))}
          </select>
        </label>
        <label className="tool-field">
          <span>Bericht</span>
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Schrijf je bericht…"
            rows={3}
            maxLength={4096}
          />
        </label>
        <label className="tool-field">
          <span>Verzenden op</span>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(event) => setScheduledAt(event.target.value)}
            min={minDateTime}
          />
        </label>
        <button
          type="button"
          className="button primary small"
          disabled={saving || !chatId || !content.trim() || !scheduledAt}
          onClick={handleCreate}
        >
          {saving ? 'Inplannen…' : 'Inplannen'}
        </button>
      </div>
      <div className="schedule-list">
        <h4>Geplande berichten</h4>
        {loading && <p className="muted small">Laden…</p>}
        {!loading && items.length === 0 && (
          <p className="empty-state small">Geen geplande berichten.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className={`schedule-item ${item.status}`}>
            <div className="schedule-item-header">
              <strong>{chatTitle(item.chatId)}</strong>
              <span className={`schedule-status ${item.status}`}>
                {statusLabel[item.status]}
              </span>
            </div>
            <p className="schedule-item-content">{item.content}</p>
            <div className="schedule-item-meta">
              <span>📅 {formatDateTime(item.scheduledAt)}</span>
              {item.status === 'pending' && (
                <button
                  type="button"
                  className="button danger small"
                  onClick={() => void handleCancel(item.id)}
                >
                  Annuleren
                </button>
              )}
              {item.error && <span className="error-text">{item.error}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SchedulePanel;
